import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback } from "react";
import Modal from "@mui/material/Modal";
import Switch from "@mui/material/Switch";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import ERRORS from "common/errors";
import { CALENDAR_FORMAT, isEndDateBeforeStartDate } from "common/calendar";
import placeholderFormat from "common/placeholder-format";
import { isMultiDayHoliday, startOfDay } from "common/holiday-calendar";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppCalendarInput from "components/app-calendar-input";

const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

/* The backend has no single/multi-day flag - it only stores startDate and a nullable
   endDate */
export const AppHolidayModal = (props, ref) => {
	const [visible, setVisible] = useState(false);
	const [holiday, setHoliday] = useState();
	const isEdit = useMemo(() => Number.isInteger(holiday?.index), [holiday]);
	const header = useMemo(() => (isEdit ? "Edit Holiday" : "Add Holiday"), [isEdit]);
	//prettier-ignore
	const initialValues = useMemo(() => ({
		title: holiday?.title || "",
		singleDay: holiday ? !isMultiDayHoliday(holiday) : true,
		startDate: holiday?.startDate || "",
		endDate: holiday?.endDate || "",
	}), [holiday]);
	const formik = useFormik({
		enableReinitialize: true,
		initialValues,
		validationSchema: yup.object({
			title: yup.string().trim().required(ERRORS.REQUIRED),
			startDate: yup.string().required(ERRORS.REQUIRED),
			endDate: yup
				.string()
				.nullable()
				.when("singleDay", {
					is: false,
					then: (schema) =>
						schema.required(ERRORS.REQUIRED).test("is-after-start-date", "End Date must be after Start Date", function (value) {
							const start = startOfDay(this.parent.startDate);
							const end = startOfDay(value);

							if (!start || !end) return true;

							return end > start;
						}),
				}),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	//prettier-ignore
	const onHandleStartDate = useCallback((name, value) => {
		formik.setFieldValue(name, value);

		if (isEndDateBeforeStartDate(value, formik.values.endDate)) formik.setFieldValue("endDate", "");
	}, [formik]);

	const onHandleShow = useCallback((data) => {
		setHoliday(data);
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		formik.resetForm();
		setHoliday();
		setVisible(false);
	}, [formik]);

	/* Switching to Single Day drops any end date so a stale value cannot be submitted,
	   and switching to Multi-Day leaves it blank for the user to pick. */
	//prettier-ignore
	const onHandleToggleSingleDay = useCallback((event) => {
		formik.setFieldValue("singleDay", event.target.checked);
		formik.setFieldValue("endDate", "");
		formik.setFieldTouched("endDate", false);
	}, [formik]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		const payload = {
			index: holiday?.index,
			title: values.title.trim(),
			startDate: values.startDate,
			endDate: values.singleDay ? null : values.endDate,
		};

		const response = await props.onHandleSave(payload);

		formik.setSubmitting(false);

		if (response) onHandleDismiss();
	}, [holiday, props, formik, onHandleDismiss]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	return (
		<Modal classes={{ root: "app-holiday-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="holiday-modal" aria-describedby="holiday-modal">
			<div className="holiday-modal">
				<div className="holiday-modal__main">
					<div className="holiday-modal__header">
						<p className="holiday-modal__title">{header}</p>
					</div>

					<form className="holiday-modal__body" onSubmit={formik.handleSubmit}>
						{/* prettier-ignore */}
						<AppInput required type="text" name="title" label="Holiday Name" placeholder="e.g. Hari Raya" value={formik.values.title} error={formik.errors.title} touched={formik.touched.title} disabled={formik.isSubmitting} onChange={formik.handleChange} />

						<div className="holiday-modal__toggle">
							<div className="holiday-modal__toggle-text">
								<p className="holiday-modal__toggle-label">Single Day</p>
							</div>

							<Switch name="singleDay" checked={formik.values.singleDay} disabled={formik.isSubmitting} onChange={onHandleToggleSingleDay} />
						</div>

						{formik.values.singleDay && (
							/* prettier-ignore */
							<AppCalendarInput required name="startDate" label="Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.startDate} error={formik.errors.startDate} touched={formik.touched.startDate} disabled={formik.isSubmitting} onChange={onHandleStartDate} />
						)}

						{!formik.values.singleDay && (
							<div className="holiday-modal__row">
								{/* prettier-ignore */}
								<AppCalendarInput required name="startDate" label="Start Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.startDate} error={formik.errors.startDate} touched={formik.touched.startDate} disabled={formik.isSubmitting} onChange={onHandleStartDate} />

								{/* prettier-ignore */}
								<AppCalendarInput required name="endDate" label="End Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.startDate} value={formik.values.endDate} error={formik.errors.endDate} touched={formik.touched.endDate} disabled={formik.isSubmitting} onChange={formik.setFieldValue} />
							</div>
						)}

						<div className="holiday-modal__button-container">
							<AppButton type="button" label="Cancel" outline disabled={formik.isSubmitting} onClick={onHandleDismiss} />
							<AppButton type="submit" label="Save" disabled={formik.isSubmitting} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppHolidayModal));

AppHolidayModal.propTypes = {
	ref: PropTypes.object,
	onHandleSave: PropTypes.func.isRequired,
};
